import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Banknote } from 'lucide-react'
import { useState } from 'react'
import { shiftQuery, tillsQuery, weekQuery, type Till } from '@/api/cash'
import type { Branch } from '@/api/types'
import { ApprovalsCard } from '@/app/cash/approvals-card'
import { CloseShiftPanel } from '@/app/cash/close-shift-panel'
import { DeskHeader } from '@/app/cash/desk-shell'
import { useCashClub } from '@/app/cash/use-cash-club'
import { OpenShiftPanel } from '@/app/cash/open-shift-panel'
import { MovementPanel, SwitchPayeePanel, XReportPanel } from '@/app/cash/shift-panels'
import { ShiftView, type ShiftAction } from '@/app/cash/shift-view'
import { useDeskNames, type DeskNames } from '@/app/cash/use-desk'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import type { Caller } from '@/domain/abilities'
import { canSeeTill, canWorkTill, cashClubs, cashErrorText, weekdayIn } from '@/domain/cash'
import { errorText } from '@/domain/errors'
import { formatMoneyColumn } from '@/domain/pass-rules'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/cash/')({
  component: CashDesk,
})

/// «Каса → Зміна» (specs/cash-desk.md §6): the club's till as the desk works
/// it. No shift open — what the drawer should hold and «Відкрити зміну»; a
/// shift open — the X: the cash expected now, the payee, the movements, and
/// «Внесення», «Інкасація», «Змінити ФОП», «X-звіт», «Закрити зміну». Seen
/// with `cash ≥ view`, worked with `cash ≥ edit` at that club; a manager
/// also sees what waits for their yes.
function CashDesk() {
  const { orgId } = Route.useParams()
  const { access, caller } = useCompanyCaller(orgId)
  const names = useDeskNames(orgId)
  const clubs = cashClubs(names.branches, access)
  const { club, pick } = useCashClub(orgId, clubs)
  const [news, setNews] = useState<string | null>(null)

  if (!canSeeTill(caller)) return <NothingForRole />

  return (
    <Page>
      <DeskHeader
        orgId={orgId}
        clubs={clubs}
        club={club}
        onPick={(branchId) => {
          setNews(null)
          pick(branchId)
        }}
      />
      <div className="flex max-w-[720px] flex-col gap-6">
        {news ? <InlineBanner kind="done" message={news} /> : null}
        <ApprovalsCard orgId={orgId} caller={caller} userId={access.userId} names={names} onNews={setNews} />
        {club ? (
          <Desk
            key={club.id}
            orgId={orgId}
            club={club}
            caller={caller}
            work={canWorkTill(caller, access, club.id)}
            names={names}
            onNews={setNews}
          />
        ) : names.branches.length > 0 ? (
          <EmptyState icon={<Banknote />} title={t.cash.desk.noClubs} />
        ) : (
          <LoadingBlock label={t.app.loading} />
        )}
      </div>
    </Page>
  )
}

/// One club's till: its open shift, or the way to open one.
const Desk = ({
  orgId,
  club,
  caller,
  work,
  names,
  onNews,
}: {
  orgId: string
  club: Branch
  caller: Caller
  work: boolean
  names: DeskNames
  onNews: (news: string | null) => void
}) => {
  const tills = useQuery(tillsQuery(orgId, club.id))
  const till: Till | null = tills.data?.[0] ?? null
  const open = till?.openShift ?? null
  const shift = useQuery({ ...shiftQuery(orgId, open?.id ?? ''), enabled: open !== null })
  const week = useQuery(weekQuery(orgId, club.id))
  const [panel, setPanel] = useState<ShiftAction | 'open' | null>(null)

  if (tills.isPending) return <LoadingBlock label={t.app.loading} />
  if (tills.isError) {
    return (
      <InlineBanner
        kind="error"
        message={errorText(tills.error, cashErrorText(names.branches))}
        action={{ label: t.app.retry, onClick: () => void tills.refetch() }}
      />
    )
  }

  // The payee of the club's today: the server's answer for a saved week,
  // the club's own clock for one never saved.
  const today = week.data?.today?.weekday ?? weekdayIn(club.timezone, new Date())
  const dayKey = String(today) as keyof NonNullable<typeof week.data>['days']
  const dayPayeeId = week.data?.today?.payeeId ?? week.data?.days[dayKey] ?? null
  const dayName = t.cash.dayNames[today - 1] ?? ''
  const done = (message: string) => {
    setPanel(null)
    onNews(message)
  }

  return (
    <>
      {!work ? <InlineBanner kind="info" message={t.cash.desk.readOnly} /> : null}
      {open === null ? (
        <section className="flex flex-col items-start gap-4 rounded-[20px] bg-[var(--card,var(--surface))] p-5 sm:p-6">
          <div className="flex flex-col gap-1">
            <span className="text-caption font-medium text-text-2">{club.name}</span>
            <h2 className="text-heading font-bold text-text">{t.cash.shift.closedTitle}</h2>
            <p className="max-w-[520px] text-callout text-text-2 text-pretty">
              {till && till.lastShiftNumber > 0
                ? t.cash.shift.closedMessage(formatMoneyColumn(till.expectedFloatMinor))
                : t.cash.shift.firstMessage}
            </p>
          </div>
          {work ? (
            <Button variant="primary" onClick={() => setPanel('open')}>
              {t.cash.shift.open}
            </Button>
          ) : null}
        </section>
      ) : shift.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(shift.error, cashErrorText(names.branches))}
          action={{ label: t.app.retry, onClick: () => void shift.refetch() }}
        />
      ) : !shift.data ? (
        <LoadingBlock label={t.app.loading} />
      ) : (
        <ShiftView
          shift={shift.data}
          clubName={club.name}
          names={names}
          work={work}
          onAction={(action) => {
            onNews(null)
            setPanel(action)
          }}
        />
      )}

      {panel === 'open' ? (
        <OpenShiftPanel
          orgId={orgId}
          club={club}
          till={till}
          caller={caller}
          names={names}
          dayPayeeId={dayPayeeId}
          dayName={dayName}
          onClose={() => setPanel(null)}
          onOpened={done}
        />
      ) : null}
      {shift.data && (panel === 'cash_in' || panel === 'collection') ? (
        <MovementPanel orgId={orgId} shift={shift.data} kind={panel} names={names} onClose={() => setPanel(null)} onDone={done} />
      ) : null}
      {shift.data && panel === 'switch' ? (
        <SwitchPayeePanel orgId={orgId} shift={shift.data} names={names} onClose={() => setPanel(null)} onDone={done} />
      ) : null}
      {shift.data && panel === 'x' ? <XReportPanel shift={shift.data} names={names} onClose={() => setPanel(null)} /> : null}
      {shift.data && panel === 'close' ? (
        <CloseShiftPanel
          orgId={orgId}
          shift={shift.data}
          caller={caller}
          names={names}
          onClose={() => setPanel(null)}
          onClosed={done}
        />
      ) : null}
    </>
  )
}
